import { useState } from "react";
import type { Menu } from "../../api/types";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { AllergenChips } from "../menu-admin/AllergenChips";
import {
  ALLERGENS,
  allergenName,
  allergenView,
  normalize,
  suggestAllergens,
  useAllergenDictionary,
} from "../menu-admin/allergens";
import { productsIn, sellableCategories } from "../sale/menu-view";

interface Props {
  menu: Menu;
  onClose: () => void;
  /** "Folder bewerken": the shop's folder in a window of its own, to change and print (Furkan, 21/10/2026). */
  onFolder?: () => void;
}

/**
 * "⚠ Allergenen" for the staff (prototype openAlgCard): search a product or code, tap what the
 * customer is allergic to and only the products without it stay, per category; "ook 'mogelijk'
 * toelaten" keeps the ones that only may contain it. A proposal that was not confirmed says so.
 */
export function AllergenCard({ menu, onClose, onFolder }: Props) {
  // Tafo-beheer can change the allergen list: the proposals follow at once
  useAllergenDictionary();
  const text = t.allergenCard;
  const [query, setQuery] = useState("");
  const [allergic, setAllergic] = useState<ReadonlySet<string>>(new Set());
  const [allowMaybe, setAllowMaybe] = useState(false);
  const typed = normalize(query);

  const sections = sellableCategories(menu).flatMap((category) => {
    const rows = productsIn(menu, category.id)
      .filter(
        (product) => !typed || normalize(product.name).includes(typed) || normalize(product.code).startsWith(typed),
      )
      .map((product) => ({ product, view: allergenView(menu, product) }))
      .filter(
        ({ view }) => ![...allergic].some((code) => view.certain.has(code) || (!allowMaybe && view.maybe.has(code))),
      );
    if (rows.length === 0) return [];
    // the base of the category, e.g. "Frituur: kruisbesmetting mogelijk."
    const note = suggestAllergens(menu, { name: "", categoryId: category.id }).note;
    return [{ category, note, rows }];
  });
  const count = sections.reduce((total, section) => total + section.rows.length, 0);
  // Tafo's list knows no "possible" any more; the choice only shows for a menu that still has one saved
  const anyMaybe = Object.values(menu.products).some((product) => (product.allergensMaybe ?? []).length > 0);
  const without = [...allergic].map((code) => allergenName(code).toLowerCase()).join(", ");

  return (
    <Dialog title={text.title} onClose={onClose}>
      <div className="algcard">
        {onFolder && (
          <button type="button" className="btn ghost algcard-folder" title={text.folderTitle} onClick={onFolder}>
            {text.folder}
          </button>
        )}
        <input
          value={query}
          placeholder={text.search}
          aria-label={text.searchLabel}
          autoComplete="off"
          data-autofocus
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <div>
          <span className="flabel">{text.allergic}</span>
          <div className="algfs">
            {ALLERGENS.map((allergen) => (
              <button
                key={allergen.code}
                type="button"
                className="btn algf"
                aria-pressed={allergic.has(allergen.code)}
                title={allergen.long}
                onClick={() => {
                  const next = new Set(allergic);
                  if (!next.delete(allergen.code)) next.add(allergen.code);
                  setAllergic(next);
                }}
              >
                {allergen.name}
              </button>
            ))}
          </div>
          {anyMaybe && (
            <label className="algmay">
              <input
                type="checkbox"
                checked={allowMaybe}
                onChange={(event) => {
                  setAllowMaybe(event.target.checked);
                }}
              />{" "}
              {text.allowMaybe}
            </label>
          )}
        </div>
        <div>
          <p className="proto algcnt">
            {allergic.size > 0 ? text.countWithout(count, without, allowMaybe) : text.count(count)}
          </p>
          {sections.length === 0 && <p className="proto">{text.none}</p>}
          {sections.map(({ category, note, rows }) => (
            <section className="algsec" key={category.id}>
              <h3>
                {category.name}
                {note && <small>{note}</small>}
              </h3>
              {rows.map(({ product, view }) => (
                <div className="algrow" key={product.id}>
                  <div className="algn">
                    <b>{product.name}</b>
                    {view.confirmed === null && <small>{text.proposal}</small>}
                  </div>
                  <div className="alcs">
                    <AllergenChips
                      certain={view.certain}
                      maybe={view.maybe}
                      suggestion={view.own ? undefined : view.suggestion}
                    />
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
        <p className="proto algfoot">{text.foot}</p>
      </div>
    </Dialog>
  );
}
